// src/components/Header/Header.js
import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  IconButton,
  Typography,
  Menu,
  Container,
  Button,
  MenuItem,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Slide,
  styled,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Close as CloseIcon,
  Phone as PhoneIcon,
} from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';

const StyledAppBar = styled(AppBar)(({ theme }) => ({
  boxShadow: 'none',
  borderBottom: '1px solid rgba(0,0,0,0.08)',
  '& .MuiToolbar-root': {
    minHeight: 80,
  },
}));

const NavButton = styled(Button)(({ theme }) => ({
  color: theme.palette.text.primary,
  fontSize: '0.95rem',
  padding: '8px 16px',
  marginLeft: theme.spacing(1),
  marginRight: theme.spacing(1),
  '&:hover': {
    backgroundColor: 'transparent',
    color: theme.palette.primary.main,
    '&::after': {
      width: '100%',
    },
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: '50%',
    transform: 'translateX(-50%)',
    width: '0%',
    height: '2px',
    backgroundColor: theme.palette.primary.main,
    transition: 'width 0.3s ease-in-out',
  },
}));

const BookNowButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.primary.light} 90%)`,
  color: 'white',
  padding: '10px 26px',
  borderRadius: '30px',
  boxShadow: '0 3px 15px rgba(46, 125, 50, 0.25)',
  '&:hover': {
    boxShadow: '0 6px 20px rgba(46, 125, 50, 0.35)',
    transform: 'translateY(-2px)',
  },
}));

const Logo = styled(Typography)(({ theme }) => ({
  fontFamily: 'Playfair Display',
  fontWeight: 700,
  fontSize: '1.8rem',
  color: theme.palette.primary.main,
  textDecoration: 'none',
  display: 'flex',
  alignItems: 'center',
  '& span': {
    color: theme.palette.text.primary,
  },
}));

const PhoneButton = styled(IconButton)(({ theme }) => ({
  marginRight: theme.spacing(2),
  color: theme.palette.primary.main,
  border: `2px solid ${theme.palette.primary.main}`,
  transition: 'all 0.3s ease',
  '&:hover': {
    backgroundColor: theme.palette.primary.main,
    color: 'white',
    transform: 'scale(1.05)',
  },
}));

function HideOnScroll({ children }) {
  const trigger = useScrollTrigger();
  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const navigationLinks = [
  { name: 'Home', path: '/' },
  { name: 'Rooms', path: '/rooms' },
  { name: 'Amenities', path: '/amenities' },
  { name: 'Contact', path: '/contact' },
];

const PHONE_NUMBER = '+917034232439';

function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const [callDialogOpen, setCallDialogOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handlePhoneClick = () => {
    setCallDialogOpen(true);
  };

  const handleCall = () => {
    window.location.href = `tel:${PHONE_NUMBER}`;
    setCallDialogOpen(false);
  };

  return (
    <>
      <HideOnScroll>
        <StyledAppBar
          position="fixed"
          sx={{
            bgcolor: isScrolled ? 'rgba(255, 255, 255, 0.95)' : 'transparent',
            transition: 'background-color 0.3s ease-in-out',
          }}
        >
          <Container maxWidth="xl">
            <Toolbar disableGutters>
              <Logo component={RouterLink} to="/">
                East Fort Holidays
              </Logo>

              {/* Desktop Navigation */}
              <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, justifyContent: 'center' }}>
                {navigationLinks.map((link) => (
                  <NavButton
                    key={link.name}
                    component={RouterLink}
                    to={link.path}
                  >
                    {link.name}
                  </NavButton>
                ))}
              </Box>

              {/* Contact & Book Now Buttons */}
              <Box sx={{ display: { xs: 'none', md: 'flex' }, alignItems: 'center' }}>
                <Tooltip title="Call Us" arrow>
                  <PhoneButton onClick={handlePhoneClick}>
                    <PhoneIcon />
                  </PhoneButton>
                </Tooltip>
                <BookNowButton
                  component={RouterLink}
                  to="/booking"
                  variant="contained"
                >
                  Book Now
                </BookNowButton>
              </Box>

              {/* Mobile Menu Icon */}
              <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
                <IconButton
                  size="large"
                  onClick={() => setMobileMenuOpen(true)}
                  sx={{ color: 'primary.main' }}
                >
                  <MenuIcon />
                </IconButton>
              </Box>
            </Toolbar>
          </Container>

          {/* Mobile Menu Drawer */}
          <Drawer
            anchor="right"
            open={mobileMenuOpen}
            onClose={() => setMobileMenuOpen(false)}
            PaperProps={{
              sx: {
                width: '100%',
                maxWidth: 360,
                bgcolor: 'background.paper',
                p: 2,
              },
            }}
          >
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
              <IconButton onClick={() => setMobileMenuOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <List>
              {navigationLinks.map((link) => (
                <ListItem
                  key={link.name}
                  component={RouterLink}
                  to={link.path}
                  onClick={() => setMobileMenuOpen(false)}
                  sx={{
                    borderRadius: 2,
                    mb: 1,
                    '&:hover': {
                      bgcolor: 'primary.light',
                      color: 'white',
                    },
                  }}
                >
                  <ListItemText primary={link.name} />
                </ListItem>
              ))}
              <ListItem>
                <Button
                  startIcon={<PhoneIcon />}
                  onClick={() => {
                    setMobileMenuOpen(false);
                    handlePhoneClick();
                  }}
                  fullWidth
                  variant="outlined"
                  color="primary"
                  sx={{ mb: 1 }}
                >
                  Call Us
                </Button>
              </ListItem>
              <ListItem>
                <BookNowButton
                  component={RouterLink}
                  to="/booking"
                  variant="contained"
                  fullWidth
                  onClick={() => setMobileMenuOpen(false)}
                >
                  Book Now
                </BookNowButton>
              </ListItem>
            </List>
          </Drawer>
        </StyledAppBar>
      </HideOnScroll>

      {/* Call Dialog */}
      <Dialog
        open={callDialogOpen}
        onClose={() => setCallDialogOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 1,
          },
        }}
      >
        <DialogTitle sx={{ textAlign: 'center' }}>
          Call East Fort Holidays
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', py: 2 }}>
          <Typography variant="h6" color="primary" gutterBottom>
            {PHONE_NUMBER}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            We're available 24/7 to assist you
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <Button
            onClick={() => setCallDialogOpen(false)}
            color="inherit"
          >
            Cancel
          </Button>
          <Button
            onClick={handleCall}
            variant="contained"
            color="primary"
            startIcon={<PhoneIcon />}
          >
            Call Now
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default Header;